/**
 * FERP Base - Main JavaScript Entry Point
 * Works alongside Keenthemes plugins.bundle.js and scripts.bundle.js
 *
 * plugins.bundle.js provides: jQuery, Bootstrap, Select2, SweetAlert2, Toastr, Flatpickr, noUiSlider, etc.
 * scripts.bundle.js provides: KTUtil, KTApp, KTMenu, KTDrawer, KTScroll, KTScrolltop, etc.
 *
 * This file adds: Additional libraries, custom utilities, and project-specific initialization
 */

// ==========================================================================
// 1. ADDITIONAL LIBRARIES (Not in plugins.bundle.js)
// ==========================================================================

// Axios for HTTP requests
import axios from 'axios';
window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// FullCalendar
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
window.FullCalendar = { Calendar, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin };

// jsTree (attaches to jQuery automatically)
import 'jstree';

// Choices.js
import Choices from 'choices.js';
window.Choices = Choices;

// Chart.js
import Chart from 'chart.js/auto';
window.Chart = Chart;

// ApexCharts
import ApexCharts from 'apexcharts';
window.ApexCharts = ApexCharts;

// Quill Editor
import Quill from 'quill';
window.Quill = Quill;

// Moment.js (for date manipulation)
import moment from 'moment';
window.moment = moment;

// CountUp.js
import { CountUp } from 'countup.js';
window.CountUp = CountUp;

// Typed.js
import Typed from 'typed.js';
window.Typed = Typed;

// PrismJS (code highlighting)
import Prism from 'prismjs';
window.Prism = Prism;

// Clipboard.js
import ClipboardJS from 'clipboard';
window.ClipboardJS = ClipboardJS;

// Alpine.js
import Alpine from 'alpinejs';
window.Alpine = Alpine;
Alpine.start();

// ==========================================================================
// 2. GLOBAL UTILITIES
// ==========================================================================

/**
 * Select2 initialization helper
 * Uses jQuery and Select2 from plugins.bundle.js
 */
window.select2 = function(root) {
    const context = root instanceof jQuery ? root[0] : (root || document);
    const elements = context.querySelectorAll('select.select2, select[data-control="select2"]');

    elements.forEach(function(element) {
        if ($(element).hasClass('select2-hidden-accessible')) {
            return;
        }

        $(element).select2({
            dropdownParent: $(element).closest('.modal').length ? $(element).closest('.modal') : $(document.body),
            placeholder: $(element).data('placeholder') || '',
            allowClear: $(element).data('allow-clear') === true
        });
    });
};

/**
 * Build hierarchical account options for dynamic select elements
 * @param {Array} accounts - Array of account objects with id, code, name, level
 * @param {string} placeholder - Placeholder text for empty option
 * @returns {string} HTML options string
 */
window.buildHierarchicalAccountOptions = function(accounts, placeholder) {
    const isRtl = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar';
    let options = placeholder ? `<option value="">${placeholder}</option>` : '<option value=""></option>';

    accounts.forEach(function(account) {
        const level = account.level || 0;
        // Use non-breaking spaces for indentation
        const indent = level > 0 ? '\u00A0\u00A0\u00A0\u00A0'.repeat(level - 1) : '';
        // RTL-aware branch character
        const branch = level > 0 ? (isRtl ? '┘ ' : '└ ') : '';
        const prefix = indent + branch;
        const displayText = account.code ? `${account.code} - ${account.name}` : account.name;
        options += `<option value="${account.id}" data-level="${level}">${prefix}${displayText}</option>`;
    });

    return options;
};

/**
 * Strip branch characters from selected option text for cleaner display
 * Use this as templateSelection option in Select2
 * @param {object} data - Select2 data object
 * @returns {string} Clean text without branch characters and indentation
 */
window.accountTemplateSelection = function(data) {
    if (!data.text) return data.text;
    // Remove branch characters (└, ┘) and leading non-breaking spaces
    return data.text.replace(/^[\u00A0\s]*(└|┘)\s*/g, '').replace(/^[\u00A0\s]+/, '');
};

/**
 * Initialize Select2 for account selects with proper templateSelection
 * @param {jQuery} $selects - jQuery collection of select elements
 * @param {object} options - Additional Select2 options
 */
window.initAccountSelect2 = function($selects, options) {
    if (typeof $.fn.select2 === 'undefined') return;

    const defaultOptions = {
        placeholder: '',
        allowClear: false,
        width: 'resolve',
        templateSelection: window.accountTemplateSelection
    };

    const mergedOptions = $.extend({}, defaultOptions, options || {});

    $selects.each(function() {
        const $select = $(this);
        if ($select.data('select2')) $select.select2('destroy');
        $select.select2(mergedOptions);
    });
};

/**
 * Choices.js initialization helper
 */
window.initChoices = function(root) {
    const context = root || document;
    const elements = context.querySelectorAll('select.choices');

    elements.forEach(function(element) {
        if (element.dataset.choicesInitialized === 'true') {
            return;
        }

        new Choices(element, {
            shouldSort: false,
            removeItemButton: true,
            searchEnabled: true,
            placeholderValue: element.getAttribute('data-placeholder') || '',
            searchPlaceholderValue: element.getAttribute('data-search-placeholder') || ''
        });

        element.dataset.choicesInitialized = 'true';
    });
};

/**
 * DataTables default configuration extension
 * DataTables is already loaded via plugins.bundle.js
 */
if (typeof $.fn.dataTable !== 'undefined') {
    $.extend(true, $.fn.dataTable.defaults, {
        responsive: true,
        language: {
            search: '',
            searchPlaceholder: 'Search...',
            lengthMenu: '_MENU_',
            info: 'Showing _START_ to _END_ of _TOTAL_ entries',
            infoEmpty: 'No entries found',
            infoFiltered: '(filtered from _MAX_ total entries)',
            zeroRecords: 'No matching records found',
            paginate: {
                first: '<i class="fas fa-angle-double-left"></i>',
                previous: '<i class="fas fa-angle-left"></i>',
                next: '<i class="fas fa-angle-right"></i>',
                last: '<i class="fas fa-angle-double-right"></i>'
            }
        }
    });
}

/**
 * Toastr default configuration extension
 * Toastr is already loaded via plugins.bundle.js
 */
if (typeof toastr !== 'undefined') {
    toastr.options = {
        closeButton: true,
        debug: false,
        newestOnTop: true,
        progressBar: true,
        positionClass: 'toast-top-right',
        preventDuplicates: false,
        onclick: null,
        showDuration: '300',
        hideDuration: '1000',
        timeOut: '5000',
        extendedTimeOut: '1000',
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut'
    };
}

// ==========================================================================
// 3. DOM READY INITIALIZATION
// ==========================================================================
document.addEventListener('DOMContentLoaded', function() {
    // Initialize Select2 globally
    window.select2();

    // Initialize Choices.js globally
    window.initChoices();

    console.log('FERP Base - Vite assets loaded successfully');
});

// ==========================================================================
// 4. EXPORT FOR MODULE USE
// ==========================================================================
export {
    axios,
    Calendar,
    Choices,
    Chart,
    ApexCharts,
    Quill,
    moment,
    CountUp,
    Typed,
    Prism,
    ClipboardJS,
    Alpine
};
